import { Fragment, useEffect } from 'react';
import { Button, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import { groupBy, sortBy, omitBy, isUndefined, isEmpty, pick, omit, orderBy, flatten, uniq, keyBy, chunk } from 'lodash';
import { useToggle } from 'react-use';
import { isEmail, isMultibyte } from 'validator';
import qs from 'qs';
import numeral from 'numeral';
import { format as formatDate } from 'date-fns';

import firebase, { functions } from '../../firebase';
import { fieldDisplayValue, ageDisplay } from '../../shared/util';
import { fields, blockSmsFields, adminFields, commentFields } from '../../shared/models/user';
import { emailDeliveryStatuses } from '../../shared/models/emailDeliveryStatus';
import AdminPage from '../hocs/AdminPage';
import { adminPages } from '../../routes';
import ModelFormModal from '../modals/ModelFormModal';
import SimilarUsersModal from '../modals/SimilarUsersModal';
import ModalButton from '../ModalButton';
import EditButton from '../EditButton';
import AddInTenantButton from '../AddInTenantButton';
import useQueryParams from '../hooks/useQueryParams';
import useFirebaseUser from '../hooks/useFirebaseUser';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useUserTags from '../hooks/useUserTags';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import QueryBoolean from '../QueryBoolean';
import { updateUserStatus } from '../../lib/firebaseFunctions';

import TenantLink from '../TenantLink';
import { generateInquiryId } from '../../shared/util';

const updateUserEmail = functions.httpsCallable('updateUserEmail');
const db = firebase.firestore();
const usersRef = db.collection('users');
const ordersRef = db.collection('orders');
const troubleInquiriesRef = db.collection('troubleInquiries');
const methodInquiriesRef = db.collection('methodInquiries');
const inquiriesRef = db.collection('inquiries');
const inquiryTypesRef = db.collection('inquiryTypes');
const entriesRef = db.collectionGroup('entries');
const productsRef = db.collection('products');
const userTagsRef = db.collection('userTags');
const activitiesRef = db.collection('activities');
const { entries } = Object;
const emailFields = {
  email: {
    type: 'string',
    inputType: 'email',
    label: 'メールアドレス',
    validations: {
      required: (v) => !isEmpty(v),
      format: (v) => !v || isEmail(v),
      notMultibyte: (v) => !v || !isMultibyte(v),
    },
  },
};

export default AdminPage(function AdminUsers(props) {
  const { firebaseUser } = useFirebaseUser();
  const {
    user: currentUser,
    match: {
      params: { userId },
    },
    tenant,
  } = props;
  const queryParams = useQueryParams();
  const [showsEmailFormModal, toggleEmailFormModal] = useToggle(false);
  const user = useDocumentSubscription(usersRef.doc(userId), [userId]);
  const tenantUser = useDocumentSubscription(tenant.ref.collection('tenantUsers').doc(userId), [userId]);
  const mainUser = useDocumentSubscription(user?.mainUserId && usersRef.doc(user.mainUserId), [user?.mainUserId]);
  const subUsers = useDocumentsFetch((user?.subUserIds || []).map(_ => usersRef.doc(_)), [user]);
  const pendingMainUser = useDocumentSubscription(user?.pendingMainUserId && usersRef.doc(user.pendingMainUserId), [user?.pendingMainUserId]);
  const pendingSubUsers = useCollectionSubscription(usersRef.where('tenantIds', 'array-contains', tenant.id).where('pendingMainUserId', '==', userId), [userId]);
  const samePhoneUsers = useCollectionSubscription(
    !isEmpty(tenantUser?.phone) && tenant.ref.collection('tenantUsers').where('phone', '==', tenantUser.phone),
    [tenantUser?.phone]
  ).filter(_ => _.id !== userId);
  const { children = [] } = user || {};
  const orders = useCollectionSubscriptionInTenant(user && ordersRef.where('createdBy.uid', '==', user.uid), [user]);
  const referredEmail = useDocumentSubscription(user && db.collection('referredEmails').doc(user.email), [user]);
  const referredEmailQrUrl = useDocumentSubscription(referredEmail && db.collection('qrUrls').doc(referredEmail.qrUrlId), [referredEmail]);
  const orderedProductIds = uniq(flatten(orders.map(({ orderItems }) => orderItems.map(({ productId }) => productId))));
  const orderedProducts = useDocumentsFetch(
    orderedProductIds.map((_) => productsRef.doc(_)),
    [orders]
  );
  const orderedProductsById = keyBy(orderedProducts, 'id');
  const envelopeProducts = useCollectionSubscriptionInTenant(db.collection('envelopeProducts'));
  const envelopeProductsById = keyBy(envelopeProducts, 'id');
  const troubleInquiries = useCollectionSubscriptionInTenant(
    user && troubleInquiriesRef.where('createdBy.uid', '==', user.uid),
    [user]
  );
  const methodInquiries = useCollectionSubscriptionInTenant(
    user && methodInquiriesRef.where('createdBy.uid', '==', user.uid),
    [user]
  );
  const inquiries = useCollectionSubscriptionInTenant(user && inquiriesRef.where('createdBy.uid', '==', user.uid), [
    user,
  ]);
  const inquiryTypes = useCollectionSubscriptionInTenant(inquiryTypesRef);
  const inquiryTypesById = keyBy(inquiryTypes, 'id');
  const eventEntries = useCollectionSubscriptionInTenant(user && entriesRef.where('createdBy.uid', '==', user.uid), [
    user,
  ]);
  const events = useDocumentsFetch(
    eventEntries.map(({ ref }) => ref?.parent.parent),
    [eventEntries]
  );
  const eventsById = keyBy(events, 'id');
  const surveyAnswers = useCollectionSubscriptionInTenant(
    user && db.collection('surveyAnswers').where('createdBy.uid', '==', user.uid),
    [user]
  );
  const surveys = useDocumentsFetch(
    surveyAnswers.map((_) => db.collection('surveys').doc(_.surveyId)),
    [surveyAnswers]
  );
  const surveysById = keyBy(surveys, 'id');
  const commentsRef = usersRef.doc(userId).collection('userComments');
  const comments = orderBy(useCollectionSubscriptionInTenant(commentsRef, [userId]), (_) => _.createdAt.toDate(), 'desc');
  const userTags = useUserTags();
  const staffs = useCollectionSubscriptionInTenant(usersRef.where('role', 'in', ['admin', 'staff']));
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const deliveryHistories = useCollectionSubscriptionInTenant(
    user && db.collectionGroup('deliveryTargets').where('uid', '==', user.uid).where('status', 'in', ['delivered', 'requested']).orderBy('createdAt'),
    [user]
  );
  const customerJourneyHistories = useCollectionSubscriptionInTenant(
    user && db.collectionGroup('deliveryHistories').where('uid', '==', user.uid).orderBy('createdAt'),
    [user]
  );
  const deliveryHistoryParents = useDocumentsFetch(
    deliveryHistories.map((_) => _.ref.parent.parent),
    [deliveryHistories]
  );
  const customerJourneyParents = useDocumentsFetch(
    customerJourneyHistories.map((_) => _.ref.parent.parent),
    [customerJourneyHistories]
  );
  const deliveryHistoryParentsByPath = keyBy(deliveryHistoryParents, 'ref.path');
  const customerJourneyParentsById = keyBy(customerJourneyParents, 'id');
  const magazineGroups = sortBy(useCollectionSubscriptionInTenant(db.collection('magazineGroups')), (_) =>
    _.createdAt.toDate()
  );
  const magazineGroupsById = keyBy(magazineGroups, 'id');
  const finishSupportActivities = useCollectionSubscriptionInTenant(user && activitiesRef.where('payload.uid', '==', user.uid).orderBy('createdAt'), [user]);
  const blockSmsActivities = finishSupportActivities.filter(_ => _.type === 'updateBlockSms');
  const accessLogs = useCollectionSubscriptionInTenant(
    user && db.collection('accessLogs').where('createdBy.uid', '==', user.uid).limit(1),
    [user]
  );
  const cancellationNotices = useCollectionSubscriptionInTenant(
    user && db.collectionGroup('cancellationNotices').where('createdBy.uid', '==', user.uid),
    [user]
  );
  const cancellationNoticeIds = cancellationNotices.map(_ => _.id);
  const cancellationNoticeChunks = chunk(cancellationNoticeIds, 10);
  const cancellationNoticeEmailHistoriesByChunk = useCollectionsFetchInTenant(
    user && cancellationNoticeChunks.length > 0
      ? cancellationNoticeChunks.map(chunkIds =>
          db.collectionGroup('emailHistories')
            .where('type', '==', 'eventCancellationNotice')
            .where('cancellationNoticeIds', 'array-contains-any', chunkIds)
            .orderBy('createdAt')
        )
      : [],
    [user, cancellationNotices]
  );
  const cancellationNoticeEmailHistories = flatten(cancellationNoticeEmailHistoriesByChunk);

  // 一括メール送信履歴
  const bulkEmailHistories = useCollectionSubscriptionInTenant(
    user && db.collection('bulkEmailHistories').where('recipientUids', 'array-contains', user.uid),
    [user]
  );

  // フォロワー向けメール送信履歴
  const eventFollowers = useCollectionSubscriptionInTenant(
    user && db.collectionGroup('eventLectureAddNotifications').where('createdBy.uid', '==', user.uid),
    [user]
  );
  const followerIds = eventFollowers.map(_ => _.id);
  const followerChunks = chunk(followerIds, 10);
  const followerEmailHistoriesByChunk = useCollectionsFetchInTenant(
    user && followerChunks.length > 0
      ? followerChunks.map(chunkIds =>
          db.collectionGroup('emailHistories')
            .where('type', '==', 'eventFollower')
            .where('followerIds', 'array-contains-any', chunkIds)
            .orderBy('createdAt')
        )
      : [],
    [user, eventFollowers]
  );
  const followerEmailHistories = flatten(followerEmailHistoriesByChunk);

  // 参加者向けメール送信履歴
  const entryIds = eventEntries.map(_ => _.id);
  const entryChunks = chunk(entryIds, 10);
  const entryEmailHistoriesByChunk = useCollectionsFetchInTenant(
    user && entryChunks.length > 0
      ? entryChunks.map(chunkIds =>
          db.collectionGroup('emailHistories')
            .where('type', '==', 'eventEntry')
            .where('entryIds', 'array-contains-any', chunkIds)
            .orderBy('createdAt')
        )
      : [],
    [user, eventEntries]
  );
  const entryEmailHistories = flatten(entryEmailHistoriesByChunk);

  const activities = orderBy(
    [
      ...(user != null ? [{ id: userId, createdAt: user.createdAt ?? ({ toDate: _ => new Date(1900, 0, 1) }), label: 'アカウント作成', description: user.createdAt == null && '(日時不明)', }] : []),
      ...orders.map((order) => {
        const { id, createdAt, orderItems, isWish = false, } = order;
        const label = `商品購入${isWish ? ' (おねだり)' : ''}`;
        const description = `${orderItems.map(({ productId }) => orderedProductsById[productId]?.name).join(', ')}`;
        const queryParams = { field: ['id'], text: id };
        const path = `/admin/orders?${qs.stringify(queryParams)}`;

        return { id, createdAt, label, description, path };
      }),
      ...orders.filter(_ => _.wishPaidAt).map((order) => {
        const { id, createdAt, orderItems, isWish = false, } = order;
        const label = `おねだり注文決済`;
        const description = `${numeral(order.amount).format()}円 ${order.payerName}`;
        const queryParams = { field: ['id'], text: id };
        const path = `/admin/orders?${qs.stringify(queryParams)}`;

        return { id, createdAt: order.wishPaidAt, label, description, path };
      }),
      ...orders.filter(_ => _.cancelledAt != null).map((order) => {
        const { id, createdAt, orderItems, cancelledAt, } = order;
        const label = '商品購入キャンセル';
        const description = `${formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}購入分 (${orderItems.map(({ productId }) => orderedProductsById[productId]?.name).join(', ')})`;
        const queryParams = { field: ['id'], text: id };
        const path = `/admin/orders?${qs.stringify(queryParams)}`;

        return { id, createdAt: cancelledAt, label, description, path };
      }),
      ...troubleInquiries.map((troubleInquiry) => {
        const { id, createdAt } = troubleInquiry;
        const label = 'お問合せ';
        const description = '不具合・組立お問合せ';
        const path = `/admin/troubleInquiries/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...methodInquiries.map((methodInquiry) => {
        const { id, createdAt } = methodInquiry;
        const label = 'お問合せ';
        const description = '乗り方お問合せ';
        const path = `/admin/methodInquiries/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...inquiries.map((inquiry) => {
        const { id, createdAt, inquiryTypeId, isAdminCreated } = inquiry;
        const label = isAdminCreated ? 'お問合せ（管理者作成）' : 'お問合せ';
        const description = inquiryTypesById[inquiryTypeId]?.subject;
        const path = `/admin/inquiries/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...eventEntries.map((entry) => {
        const {
          id,
          createdAt,
          ref: {
            parent: {
              parent: { id: eventId },
            },
          },
        } = entry;
        const label = 'イベント申込';
        const description = eventsById[eventId]?.name;
        const path = `/admin/events/${eventId}/entries/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...eventEntries.filter(_ => _.cancelledAt != null).map((entry) => {
        const { id, ref, createdAt, cancelledAt, } = entry;
        const label = 'イベント申込キャンセル';
        const description = `${formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}申込み分 (${eventsById[ref.parent.parent.id]?.name})`;
        const path = `/admin/events/${ref.parent.parent.id}/entries/${id}`;

        return { id, createdAt: cancelledAt, label, description, path };
      }),
      ...surveyAnswers.map((surveyAnswer) => {
        const { id, surveyId, createdAt } = surveyAnswer;
        const label = 'アンケートページ回答';
        const description = surveysById[surveyId]?.title;
        const path = `/admin/surveyAnswers/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...deliveryHistories.map((deliveryHistory) => {
        const { id, ref, createdAt, sendgrid } = deliveryHistory;
        const deliveryHistoryParent = deliveryHistoryParentsByPath[ref.parent.parent.path];
        const { label, description } =
          {
            magazines: {
              label: 'SMS配信',
              description: deliveryHistoryParent?.body,
            },
            smsDeliveries: {
              label: 'SMS配信',
              description: deliveryHistoryParent?.emailSubject || deliveryHistoryParent?.body,
            },
            envelopeSchedules: {
              label: '郵便発送処理',
              description: (deliveryHistoryParent?.envelopeProductIds || [])
                .map((_) => envelopeProductsById[_]?.name || '')
                .join(', '),
            },
            envelopeDeliveries: {
              label: '郵便発送処理',
              description: (deliveryHistoryParent?.envelopeProductIds || [])
                .map((_) => envelopeProductsById[_]?.name || '')
                .join(', '),
            },
            customerJourneys: {
              label: 'カスタマージャーニー配信',
              description: deliveryHistoryParent?.name || deliveryHistoryParent?.title,
            },
          }[ref.parent.parent.parent.id] || {};
        const path = `/admin/${ref.parent.parent.path}`;
        const status = emailDeliveryStatuses[sendgrid?.status];

        return {
          id,
          createdAt,
          label,
          description: status != null ? (
            <div>
              <div>{description}</div>
              <div className="mt-1">
                <Badge color={status.color}>{status.label}</Badge>
                {sendgrid?.reason && <span className="small text-muted ml-1">{sendgrid.reason}</span>}
              </div>
            </div>
          ) : description,
          path,
        };
      }),
      ...(queryParams.showsCustomerJourneyActivities === '1' ? customerJourneyHistories.map((customerJourneyHistory) => {
        const { id, createdAt, customerJourneyId, customerJourneyRowKey, deliveryStatus, sendgrid } = customerJourneyHistory;
        const customerJourney = customerJourneyParentsById[customerJourneyId];
        const customerJourneyRow = customerJourney?.customerJourneyRows?.find(row => row.key === customerJourneyRowKey);
        const label = 'カスタマージャーニー配信';
        const name = `${customerJourney?.name || customerJourney?.title || '(削除済み)'}${customerJourneyRow?.name ? ` - ${customerJourneyRow.name}` : ''}`;
        const status = emailDeliveryStatuses[deliveryStatus];
        const description = (
          <div>
            <div>{name}</div>
            {status != null && (
              <div className="mt-1">
                <Badge color={status.color}>{status.label}</Badge>
                {sendgrid?.reason && <span className="small text-muted ml-1">{sendgrid.reason}</span>}
              </div>
            )}
          </div>
        );
        const path = `/admin/customerJourneys/${customerJourneyId}`;

        return { id, createdAt, label, description, path };
      }) : []),
      ...finishSupportActivities.filter(_ => _.type === 'finishSupportOfInquiry').map((finishSupportActivity) => {
        const { id, createdBy, createdAt, payload } = finishSupportActivity;
        const label = '問合せ完了';
        const description = createdBy.displayName;
        const path = `/admin/inquiries/${payload.inquiryId}`;
        return { id, createdAt, label, description, path };
      }),
      ...finishSupportActivities.filter(_ => _.type === 'finishSupportOfTroubleInquiry').map((finishSupportActivity) => {
        const { id, createdBy, createdAt, payload } = finishSupportActivity;
        const label = '不具合問合せ完了';
        const description = createdBy.displayName;
        const path = `/admin/troubleInquiries/${payload.troubleInquiryId}`;
        return { id, createdAt, label, description, path };
      }),
      ...finishSupportActivities.filter(_ => _.type === 'checkCouponOfOrder').map((activity) => {
        const { id, createdBy, createdAt, payload } = activity;
        const label = '優待確認';
        const description = createdBy.displayName;
        const path = `/admin/orders/${payload.orderId}`;
        return { id, createdAt, label, description, path };
      }),
      ...finishSupportActivities.filter(_ => _.type === 'uncheckCouponOfOrder').map((activity) => {
        const { id, createdBy, createdAt, payload } = activity;
        const label = '優待確認解除';
        const description = createdBy.displayName;
        const path = `/admin/orders/${payload.orderId}`;
        return { id, createdAt, label, description, path };
      }),
      ...blockSmsActivities.map((activity) => {
        const { id, createdBy, createdAt, payload } = activity;
        const label = '配信停止設定変更';
        const description = payload.note;
        return { id, createdAt, label, description };
      }),
      ...[referredEmail != null && {
        ...pick(referredEmail, ['id', 'createdAt']),
        label: '紹介QRコード遷移先へのリダイレクト前のメールアドレス入力',
        description: referredEmailQrUrl?.name,
        path: `/referralRedirect?${qs.stringify({ qrUrlId: referredEmailQrUrl?.id, redirectUrl: referredEmailQrUrl?.redirectUrl, referrerKey: referredEmail?.referrerKey })}`,
      }].filter(_ => _),
      ...cancellationNoticeEmailHistories.map(emailHistory => {
        const { id, createdAt, subject, ref } = emailHistory;
        const eventId = ref.parent.parent.id;
        const label = 'イベントキャンセル待ちメール送信';
        const description = subject;
        const path = `/admin/events/${eventId}/emailHistories/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...followerEmailHistories.map(emailHistory => {
        const { id, createdAt, subject, ref } = emailHistory;
        const eventId = ref.parent.parent.id;
        const label = 'イベントフォロワーメール送信';
        const description = subject;
        const path = `/admin/events/${eventId}/emailHistories/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...entryEmailHistories.map(emailHistory => {
        const { id, createdAt, subject, ref } = emailHistory;
        const eventId = ref.parent.parent.id;
        const label = 'イベント参加者メール送信';
        const description = subject;
        const path = `/admin/events/${eventId}/emailHistories/${id}`;

        return { id, createdAt, label, description, path };
      }),
      ...bulkEmailHistories.map(emailHistory => {
        const { id, createdAt, subject } = emailHistory;
        const label = '一括メール送信';
        const description = subject;
        const path = `/admin/bulkEmailHistories/${id}`;

        return { id, createdAt, label, description, path };
      }),
    ].filter(_ => _.createdAt.toDate),
    ({ createdAt }) => createdAt.toDate(),
    'asc'
  );
  const onSubmitEmailForm = async (values) => {
    try { 
      const { data } = await updateUserEmail({ userId, ...values });
      if (data && data.error) {
        console.error(data.error);
        toast.error(data.error);
        return;
      }
      await user?.ref?.update({
        ...values,
        updatedBy: omitBy(pick(firebaseUser, ['uid', 'email', 'displayName']), isUndefined),
      });
      toggleEmailFormModal(false);
      toast.success('メールアドレスを変更しました');
    } catch(e) {
      console.error(e);
      toast.error('メールアドレスの変更に失敗しました');
    }
  };
  const adminOrdersQueryParams = {
    field: ['createdBy.uid'],
    text: user?.uid || '',
  };
  const adminOrdersPath = `/admin/orders?${qs.stringify(adminOrdersQueryParams)}`;
  const adminTroubleInquiriesQueryParams = {
    field: ['createdBy.uid'],
    text: user?.uid || '',
  };
  const adminTroubleInquiriesPath = `/admin/troubleInquiries?${qs.stringify(adminTroubleInquiriesQueryParams)}`;
  const adminInquiriesQueryParams = {
    field: ['createdBy.uid'],
    text: user?.uid || '',
  };
  const adminInquiriesPath = `/admin/inquiries?${qs.stringify(adminInquiriesQueryParams)}`;
  const adminEntriesQueryParams = {
    field: ['createdBy.uid'],
    text: user?.uid || '',
  };
  const adminEntriesPath = `/admin/entries?${qs.stringify(adminEntriesQueryParams)}`;
  const AdminSurveyAnswersPath = `/admin/users/${user?.uid}/surveyAnswers`;
  const adminAccessLogsPath = `/admin/users/${user?.uid}/accessLogs`
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [userId]);

  const toggleUserStatus = async (nextStatus) => {
    if (!window.confirm(`本当に${nextStatus === 'enable' ? '有効' : '無効'}にしますか？`)) return;
    try {
      await updateUserStatus({ uid: userId, nextStatus });
      toast.success(`${nextStatus === 'enable' ? '有効' : '無効'}にしました`);
    } catch (e) {
      toast.error('失敗しました');
    }
  };

  const onCreateUserTag = async (value) => {
    const ref = await userTagsRef.add({ tenantId: tenant.id, name: value, createdAt: new Date() });
    return ref.id;
  };

  return (
    user != null && tenantUser != null && (
      <div className="admin-user container-fluid">
        <div className="p-4 bg-white my-4">
          <div className="d-flex justify-content-center mb-3">
            <h4>
              アカウント詳細
              {!isEmpty(user.mainUserId) && <div className="ml-1 badge badge-warning">サブアカウント</div>}
            </h4>
          </div>
          {!isEmpty(samePhoneUsers) && (
            <div className="alert alert-warning">
              <div className="font-weight-bold">
                <span className="fas fa-exclamation-triangle mr-1" />
                電話番号が一致するアカウントが{samePhoneUsers.length}件あります
              </div>
              <div className="mt-2 d-flex flex-column gap-1">
                {samePhoneUsers.map(({ id, displayName, email }) => (
                  <div key={id}>
                    <TenantLink to={`/admin/users/${id}`} target="_blank">
                      {displayName}（{email}）
                      <span className="fas fa-external-link-alt ml-1" />
                    </TenantLink>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="d-flex justify-content-end mb-3 gap-2 flex-wrap text-nowrap">
            <ModalButton
              Modal={SimilarUsersModal}
              modalProps={{ tenant, user: tenantUser }}
              label="類似アカウント検索"
            />
            {!isEmpty(currentUser.trs110User) && (
              <Button tag="a" href={`http://192.168.241.103/login?user=${encodeURI(currentUser.trs110User)}&password=${btoa(currentUser.trs110Password)}&partyAddress=${user.phone}`} target='_blank'>
                TRS110
                <span className="fas fa-external-link-alt ml-1" />
              </Button>
            )}
            {isEmpty(orders) || (
              <Button tag={TenantLink} to={adminOrdersPath} target="_blank">
                注文一覧
              </Button>
            )}
            {isEmpty(eventEntries) || (
              <Button tag={TenantLink} to={adminEntriesPath} target="_blank">
                イベント申し込み一覧
              </Button>
            )}
            {isEmpty(troubleInquiries) || (
              <Button tag={TenantLink} to={adminTroubleInquiriesPath} target="_blank">
                不具合・組立お問合せ一覧
              </Button>
            )}
            {isEmpty(inquiries) || (
              <Button tag={TenantLink} to={adminInquiriesPath} target="_blank">
                お問合せ一覧
              </Button>
            )}
            {isEmpty(surveyAnswers) || (
              <Button tag={TenantLink} to={AdminSurveyAnswersPath} target="_blank">
                アンケート回答一覧
              </Button>
            )}
            {isEmpty(accessLogs) || (
              <Button tag={TenantLink} to={adminAccessLogsPath} target="_blank">
                アクセスログ一覧
              </Button>
            )}
            <Button onClick={toggleEmailFormModal.bind(null, true)}>メールアドレス変更</Button>
            <ModalButton Modal={ModelFormModal} modalProps={{
              title: '問合せの作成',
              fields: {
                description: {
                  label: '内容',
                  type: 'text',
                  validations: {
                    required: _ => !isEmpty(_),
                  },
                },
              },
              onSubmit: async ({ description }) => {
                try {
                  const id = await generateInquiryId();
                  await inquiriesRef.doc(id).set({
                    name: user.displayName,
                    ...omitBy(pick(user, ['nameKana', 'phone', 'email', 'postalCode', 'prefecture', 'city', 'address']), isEmpty),
                    description,
                    tenantId: tenant.id,
                    files: [],
                    createdAt: new Date(),
                    createdBy: user,
                    myAccount: user,
                    status: 'initial',
                    isAdminCreated: true
                  });
                  toast.success('問い合わせを作成しました');
                } catch(e) {
                  console.error(e);
                  toast.error('失敗しました');
                }
              },
            }}>
              問合せの作成
            </ModalButton>
            <EditButton
              label="権限"
              beforeSubmit={_ => ['staff', 'admin'].includes(_.role) ? { ..._, tenantId: user.tenantId || tenant.id } : _}
              itemRef={user.ref}
              FormModal={ModelFormModal}
              formProps={{
                title: '権限編集',
                fields: pick(adminFields({
                  userTags,
                  onCreateUserTag: onCreateUserTag,
                  staffs,
                  productTypes,
                  adminPages,
                }), ['role', 'allowedAdminPages']),
              }}
            />
            <EditButton
              itemRef={tenantUser.ref}
              FormModal={ModelFormModal}
              beforeSubmit={async (values) => {
                await user.ref.update({ ...pick(values, [`blocksSms__${tenant.id}`, `blocksMagazineGroupIds__${tenant.id}`]), });
                return omit(values, [`blocksSms__${tenant.id}`, `blocksMagazineGroupIds__${tenant.id}`]);
              }}
              formProps={{
                title: '編集',
                fields: {
                  ...pick(adminFields({
                    userTags,
                    onCreateUserTag: onCreateUserTag,
                    staffs,
                    productTypes,
                  }), ['ngDm', 'note', 'userTagIds', 'salesStatReadble', 'salesStatFilterProductTypes', 'salesStatFilterRoutes', 'assigneeId']),
                  ...blockSmsFields({ tenants: [tenant], magazineGroupsGroupedByTenantId: groupBy(magazineGroups, 'tenantId') }),
                },
              }}
            />
            {user.disabledAt ? (
              <Button color="warning" onClick={() => toggleUserStatus('enable')}>
                有効にする
              </Button>
            ) : (
              <Button color="danger" onClick={() => toggleUserStatus('disable')}>
                無効にする
              </Button>
            )}
          </div>
          <div className="d-sm-flex justify-content-around mt-5 gap-4">
            <div className="flex-fill">
              <table className='table table-bordered'>
                <tbody className='thead-light'>
                  {entries({
                    ...omit(fields({ magazineGroups }), [
                      'password',
                      'currentPassword',
                      'passwordConfirmation',
                      'accept',
                    ]),
                    ...adminFields({ adminPages, userTags, staffs, productTypes }),
                    // TODO: fieldSettingの型定義する
                  }).map(([fieldName, fieldSetting]) => {
                    const { label } = fieldSetting;
                    const displayValue = fieldDisplayValue(tenantUser[fieldName], fieldSetting);
                    return (
                      <tr key={fieldName}>
                        <th style={{ width: 200 }}>{label}</th>
                        <td style={{ whiteSpace: 'pre-line' }}>
                          {Array.isArray(displayValue) ? displayValue.join(', ') : displayValue}
                        </td>
                      </tr>
                    );
                  })}
                <tr>
                  <th style={{ width: 200 }}>SMS/メール配信を停止する</th>
                  <td>
                    {user[`blocksSms__${tenant.id}`] ? 'YES' : 'NO'}
                  </td>
                </tr>
                <tr>
                  <th style={{ width: 200 }}>SMS/メール配信停止グループ</th>
                  <td>
                    {(user[`blocksMagazineGroupIds__${tenant.id}`] || []).map(_ => magazineGroupsById[_]?.name).filter(_ => _).join(', ')}
                  </td>
                </tr>
              </tbody>
            </table>
            {
              ((mainUser != null && !isEmpty(user.mainUserId)) || (pendingMainUser != null && !isEmpty(user.pendingMainUserId))) && (
                <Fragment>
                  <hr className='my-5' />
                  <div>
                    <div className='d-flex justify-content-center mb-3'>
                      <h4>メインアカウント</h4>
                    </div>
                    <div className='overflow-auto'>
                      <table className='table table-bordered'>
                        <thead className='thead-light ext-nowrap'>
                          <tr>
                            <th style={{ minWidth: 100 }}>名前</th>
                            <th style={{ minWidth: 120 }}>メールアドレス</th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            mainUser != null && (
                              <tr>
                                <td>
                                  <TenantLink to={`/admin/users/${mainUser.id}`}>{mainUser.displayName}</TenantLink>
                                </td>
                                <td>
                                  {mainUser.email}
                                </td>
                              </tr>
                            )
                          }
                          {
                            pendingMainUser != null && (
                              <tr>
                                <td>
                                  <TenantLink to={`/admin/users/${pendingMainUser.id}`}>{pendingMainUser.displayName}</TenantLink>
                                  <span className="badge badge-warning mx-2">承認待ち</span>
                                </td>
                                <td>
                                  {pendingMainUser.email}
                                </td>
                              </tr>
                            )
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Fragment>
              )
            }
            {
              [...subUsers, ...pendingSubUsers].length !== 0 && (
                <Fragment>
                  <hr className='my-5' />
                  <div>
                    <div className='d-flex justify-content-center mb-3'>
                      <h4>サブアカウント</h4>
                    </div>
                    <div className='overflow-auto'>
                      <table className='table table-bordered'>
                        <thead className='thead-light ext-nowrap'>
                          <tr>
                            <th style={{ minWidth: 100 }}>名前</th>
                            <th style={{ minWidth: 120 }}>メールアドレス</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subUsers.map((subUser) => {
                            const { id, displayName, email, } = subUser;

                            return (
                              <tr key={id}>
                                <td>
                                  <TenantLink to={`/admin/users/${id}`}>{displayName}</TenantLink>
                                </td>
                                <td>
                                  {email}
                                </td>
                              </tr>
                            );
                          })}
                          {pendingSubUsers.map((subUser) => {
                            const { id, displayName, email, } = subUser;
                            return (
                              <tr key={id}>
                                <td>
                                  <TenantLink to={`/admin/users/${id}`}>{displayName}</TenantLink>
                                  <span className="badge badge-warning mx-2">承認待ち</span>
                                </td>
                                <td>
                                  {email}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Fragment>
              )
            }
            <hr className="my-5" />
            <div>
              <div className="d-flex justify-content-center mb-3">
                <h4>お子様情報</h4>
              </div>
              <div className="overflow-auto">
                <table className="table table-bordered">
                  <thead className="thead-light ext-nowrap">
                    <tr>
                      <th style={{ minWidth: 100 }}>名前</th>
                      <th style={{ minWidth: 120 }}>誕生日</th>
                      <th style={{ minWidth: 200 }}>乗り物経験</th>
                    </tr>
                  </thead>
                  <tbody>
                    {children.map((child, index) => {
                      const { name, birthday, vehicleExperiences } = child;

                      return (
                        <tr key={index}>
                          <td>{name}</td>
                          <td>
                            <div className="d-flex gap-1">
                              <span>{formatDate(birthday.toDate(), 'yyyy/MM/dd')}</span>
                              <span>({ageDisplay(birthday.toDate())})</span>
                            </div>
                          </td>
                          <td>{vehicleExperiences?.join(', ')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="mt-5 mt-sm-0" style={{ minWidth: '40vw', maxWidth: window.innerWidth >= 576 ? '50vw' : 'none' }}>
            <div>
              <div className='d-flex justify-content-end mb-3'>
                <AddInTenantButton
                  label='新規コメント'
                  itemRef={commentsRef.doc()}
                  processValues={(_) => ({
                    ..._,
                    createdBy: pick(firebaseUser, ['uid', 'email', 'displayName']),
                  })}
                  FormModal={ModelFormModal}
                  formProps={{ title: '新規コメント', fields: commentFields }}
                />
              </div>
              <div className="overflow-auto" style={{ maxHeight: 360 }}>
                {comments.length > 0 ? (
                  <div>
                    {comments.map(({ id, body, createdAt, createdBy }) => {
                      return (
                        <div key={id} className='card p-3 mb-3'>
                          <div className='small text-muted mb-1'>
                            {createdBy?.displayName} {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                          </div>
                          <div className="text-break" style={{ whiteSpace: 'pre-line' }}>{body}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>コメントはまだありません</div>
                )}
              </div>
            </div>
            <hr className='my-5' />
            <div>
              <h5>アクティビティ</h5>
              <div className="mt-2 d-flex align-items-end flex-wrap gap-2">
                <QueryBoolean paramName="showsCustomerJourneyActivities" label="カスタマージャーニーのアクティビティを表示" />
              </div>
              <div>
                <table className="table table-borderless">
                  <tbody>
                    {activities.map((activity, index) => {
                      const { createdAt, label, description, path } = activity;

                      return (
                        <tr key={index}>
                          <td style={{ minWidth: 160, maxWidth: 160 }}>
                            {formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm')}
                          </td>
                          <td style={{ minWidth: 160, maxWidth: 160 }}>
                            <TenantLink to={path} target="_blank">
                              {label}
                            </TenantLink>
                          </td>
                          <td style={{ minWidth: 200 }}>{description}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        </div>
        {showsEmailFormModal && (
          <ModelFormModal
            isOpen
            fields={emailFields}
            title="メールアドレス変更"
            onSubmit={onSubmitEmailForm}
            onClickClose={toggleEmailFormModal.bind(null, false)}
          />
        )}
      </div>
    )
  );
});
